/**
 * @file PromptDialog.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-24
 * @modified 2026-08-24
 * @description Ask for one short piece of text, the way ConfirmDialog asks for a
 *              yes or no.
 *
 *              The system already has Alert, AlertDialog and ConfirmDialog but
 *              nothing for "type a name" — which otherwise means a bespoke modal
 *              each time. Confirm is disabled while the field is empty, so the
 *              dialog cannot be used to submit nothing.
 */

import React, { useState } from 'react';
import Modal from './Modal';
import Input from './Input';
import { useSlideOutButton } from './SlideOutButton';

function PromptDialogBody({ message, value, setValue, placeholder, onSubmit }) {
  // Mounts with the buttons, so the slide-out measurement happens in the same
  // render cycle they appear in — see the modal rules in CLAUDE.md.
  useSlideOutButton([value]);

  return (
    <div className="prompt-dialog-body">
      {message && <p className="prompt-dialog-message">{message}</p>}
      {/* autoFocus rather than a ref: Input spreads ...props onto the native
          input but is not a forwardRef, so a ref would silently do nothing. */}
      <Input
        autoFocus
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && value.trim()) onSubmit(); }}
      />
    </div>
  );
}

function PromptDialog({
  isOpen,
  title,
  message,
  placeholder = '',
  initialValue = '',
  confirmLabel = 'Confirm',
  onConfirm,
  onCancel
}) {
  const [value, setValue] = useState(initialValue);

  if (!isOpen) return null;

  const submit = () => { if (value.trim()) onConfirm(value.trim()); };

  return (
    <Modal
      isOpen
      onClose={onCancel}
      title={title}
      className="prompt-dialog"
      footer={
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onCancel}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" onClick={submit} disabled={!value.trim()}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">check</span></div>
              <span className="btn-label">{confirmLabel}</span>
            </button>
          </div>
        </div>
      }
    >
      <PromptDialogBody
        message={message}
        value={value}
        setValue={setValue}
        placeholder={placeholder}
        onSubmit={submit}
      />
    </Modal>
  );
}

export default PromptDialog;
